// src/activity/PickupMarksReport.jsx — System → Activity → Team Portal Order Pickups (Activity Reporting permission).
// Every "Ready for Pickup" and "Picked up" mark on a corporate store's pickup orders, per store-local day, split by where it
// was done: OpsHub (Store Pickup Portal / order page) or directly in Shopify admin / POS. Replaces the one-off artifact.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, BlockStack, InlineStack, Text, Banner, Button, ButtonGroup, Spinner, Box } from '@shopify/polaris';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../AuthComponent';
import { ReportDateRange, isoDaysAgo, todayIso, downloadCsv } from '../Reporting';

const OPS = '#2a78d6', SHOP = '#eb6834';
const L3 = 3;
const fmtDay = d => new Date(d + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const fmtN = n => Number(n || 0).toLocaleString();

function Tile({ label, value }) {
    return (
        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="050"><Text as="p" variant="bodySm" tone="subdued">{label}</Text><Text as="p" variant="headingLg">{value}</Text></BlockStack>
        </Box>);
}

function StoreChart({ l, days, val, onTip }) {
    const W = 300, H = 120, pl = 26, pb = 18, pt = 6, gw = (W - pl - 4) / days.length;
    const max = Math.max(1, ...l.days.map(r => val(r, 'opshub') + val(r, 'shopify')));
    const nice = max <= 5 ? 5 : max <= 10 ? 10 : Math.ceil(max / 10) * 10;
    const y = v => pt + (H - pt - pb) * (1 - v / nice);
    return (
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${l.name}: ${l.o} OpsHub, ${l.s} Shopify`} style={{ display: 'block', width: '100%', height: 'auto' }}>
            {[0, 0.5, 1].map(f => (
                <g key={f}>
                    <line x1={pl} x2={W} y1={y(nice * f)} y2={y(nice * f)} stroke="#e1e3e5" strokeWidth="1" />
                    <text x={pl - 4} y={y(nice * f) + 3} textAnchor="end" fontSize="9" fill="#8c9196">{Math.round(nice * f)}</text>
                </g>))}
            {l.days.map((r, i) => {
                const o = val(r, 'opshub'), s = val(r, 'shopify'), x = pl + i * gw + 2, bw = Math.max(4, gw - 4);
                const y0 = y(0), yo = y(o), ys = y(o + s);
                return (
                    <g key={i}>
                        {o ? <rect x={x} y={yo} width={bw} height={Math.max(1, y0 - yo)} fill={OPS} /> : null}
                        {s ? <rect x={x} y={ys} width={bw} height={Math.max(1, yo - ys - (o ? 2 : 0))} fill={SHOP} rx="2" /> : null}
                        {((i % 3 === 0 && i < days.length - 2) || i === days.length - 1)
                            ? <text x={x + bw / 2} y={H - 3} textAnchor="middle" fontSize="9" fill="#8c9196">{fmtDay(days[i]).replace(' ', ' ')}</text> : null}
                        <rect x={x - 2} y={pt} width={bw + 4} height={H - pt} fill="transparent"
                              onMouseMove={e => onTip({ x: e.clientX, y: e.clientY, l: l.name, d: days[i], r, o, s })} onMouseLeave={() => onTip(null)} />
                    </g>);
            })}
        </svg>);
}

export default function PickupMarksReport() {
    const fetchAuth = useAuthFetch();
    const [sp, setSp] = useSearchParams();
    const [from, setFrom] = useState(sp.get('from') || isoDaysAgo(13));
    const [to, setTo] = useState(sp.get('to') || todayIso());
    const [kind, setKind] = useState(sp.get('kind') || 'all');
    const [sort, setSort] = useState(sp.get('sort') || 'volume');
    const [table, setTable] = useState(sp.get('view') === 'table');
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [tip, setTip] = useState(null);
    const first = useRef(true);

    const writeUrl = useCallback((o) => setSp(prev => {
        const n = new URLSearchParams(prev);
        Object.entries(o).forEach(([k, v]) => (v === '' || v == null ? n.delete(k) : n.set(k, v)));
        return n;
    }, { replace: true }), [setSp]);

    const run = useCallback(async (over = {}) => {
        const f = { from, to, ...over };
        setBusy(true); setErr(null); writeUrl({ from: f.from, to: f.to });
        try {
            const r = await fetchAuth(`/api/activity/pickup-marks?${new URLSearchParams({ since: f.from, until: f.to })}`);
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            setData(await r.json());
        } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
    }, [fetchAuth, from, to, writeUrl]);
    useEffect(() => { if (first.current) { first.current = false; run(); } /* eslint-disable-next-line */ }, []);
    useEffect(() => { writeUrl({ kind: kind === 'all' ? '' : kind, sort: sort === 'volume' ? '' : sort, view: table ? 'table' : '' }); }, [kind, sort, table, writeUrl]);

    const days = data?.days || [];
    const val = useCallback((row, src) => (kind === 'all' ? row.ready[src] + row.picked[src] : row[kind][src]), [kind]);
    const flagged = useMemo(() => {
        const last = (data?.locs || []).map(l => {
            let o = 0, s = 0;
            for (const r of l.days.slice(-L3)) { o += r.ready.opshub + r.picked.opshub; s += r.ready.shopify + r.picked.shopify; }
            return { name: l.name, o3: o, s3: s };
        }).filter(x => x.s3 > 0);
        return last.sort((a, b) => b.s3 - a.s3 || (a.o3 / (a.o3 + a.s3)) - (b.o3 / (b.o3 + b.s3))).slice(0, 3);
    }, [data]);
    const flagRank = useMemo(() => new Map(flagged.map((x, i) => [x.name, i + 1])), [flagged]);
    const locs = useMemo(() => {
        const rows = (data?.locs || []).map(l => {
            let o = 0, s = 0;
            for (const r of l.days) { o += val(r, 'opshub'); s += val(r, 'shopify'); }
            return { ...l, o, s, t: o + s, p: o + s ? o / (o + s) : 0 };
        }).filter(l => l.t > 0);
        rows.sort((a, b) => sort === 'volume' ? b.t - a.t : sort === 'share' ? a.p - b.p || b.t - a.t : a.name.localeCompare(b.name));
        rows.sort((a, b) => (flagRank.get(a.name) || 99) - (flagRank.get(b.name) || 99));
        return rows;
    }, [data, val, sort, flagRank]);
    const O = locs.reduce((a, l) => a + l.o, 0), S = locs.reduce((a, l) => a + l.s, 0);

    const exportCsv = () => downloadCsv(`team-portal-order-pickups-${from}-${to}.csv`,
        [['Store', 'Marks', 'OpsHub', 'Shopify', 'OpsHub share'], ...locs.map(l => [l.name, l.t, l.o, l.s, `${Math.round(100 * l.p)}%`])]);

    return (
        <BlockStack gap="300">
            {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
            <Card>
                <BlockStack gap="300">
                    <Text as="p">Every "Ready for Pickup" and "Picked up" mark on a corporate store's pickup orders, day by day, split by where it was done: in <b>OpsHub</b> (Store Pickup Portal or the order page) or directly in <b>Shopify</b> admin / POS. Each panel is one store; bars stack OpsHub under Shopify.</Text>
                    <InlineStack gap="200" blockAlign="center" wrap><ReportDateRange dateFrom={from} setDateFrom={setFrom} dateTo={to} setDateTo={setTo} /></InlineStack>
                    <InlineStack gap="300" blockAlign="center" wrap>
                        <Button variant="primary" onClick={() => run()} loading={busy}>Run Report</Button>
                        <ButtonGroup variant="segmented">
                            {[['all', 'All marks'], ['ready', 'Ready for pickup'], ['picked', 'Picked up']].map(([k, t]) => <Button key={k} pressed={kind === k} onClick={() => setKind(k)}>{t}</Button>)}
                        </ButtonGroup>
                        <ButtonGroup variant="segmented">
                            {[['volume', 'Busiest first'], ['share', 'Least OpsHub first'], ['name', 'A – Z']].map(([k, t]) => <Button key={k} pressed={sort === k} onClick={() => setSort(k)}>{t}</Button>)}
                        </ButtonGroup>
                        <InlineStack gap="200" blockAlign="center">
                            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: OPS, borderRadius: 3, marginRight: 6, verticalAlign: -2 }} />OpsHub</span>
                            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: SHOP, borderRadius: 3, marginRight: 6, verticalAlign: -2 }} />Shopify</span>
                        </InlineStack>
                        <Button pressed={table} onClick={() => setTable(t => !t)}>Table view</Button>
                        <Button onClick={exportCsv} disabled={!data}>Export CSV</Button>
                    </InlineStack>
                </BlockStack>
            </Card>
            {!data ? <InlineStack align="center"><Spinner accessibilityLabel="Loading" /></InlineStack> : (
                <>
                    <Banner tone={flagged.length ? 'warning' : 'success'} title={`Past ${L3} days (${fmtDay(days[Math.max(0, days.length - L3)])} – ${fmtDay(days[days.length - 1])}) · pickups marked outside OpsHub`}>
                        {flagged.length ? (
                            <InlineStack gap="400" wrap>
                                {flagged.map(x => <span key={x.name}><b>{x.name}</b> {x.s3} in Shopify · {x.o3} in OpsHub · {Math.round(100 * x.o3 / (x.o3 + x.s3))}% OpsHub</span>)}
                            </InlineStack>
                        ) : 'Every store marked its pickups in OpsHub.'}
                    </Banner>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
                        <Tile label={`Marks in ${days.length} days`} value={fmtN(O + S)} />
                        <Tile label="In OpsHub" value={fmtN(O)} />
                        <Tile label="In Shopify" value={fmtN(S)} />
                        <Tile label="OpsHub share" value={`${Math.round(100 * O / Math.max(1, O + S))}%`} />
                        <Tile label="Corporate stores" value={fmtN(locs.length)} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                        {locs.map(l => {
                            const fr = flagRank.get(l.name);
                            return (
                                <div key={l.name} style={{ border: '1px solid #e1e3e5', borderLeft: fr ? `4px solid ${SHOP}` : '1px solid #e1e3e5', borderRadius: 8, padding: '10px 12px 6px', background: '#fff' }}>
                                    <InlineStack align="space-between" blockAlign="baseline" gap="200">
                                        <span style={{ fontWeight: 600 }}>{l.name}{fr ? <span style={{ fontSize: 10.5, fontWeight: 600, color: SHOP, border: `1px solid ${SHOP}`, borderRadius: 999, padding: '1px 7px', marginLeft: 6 }}>#{fr} outside OpsHub</span> : null}</span>
                                        <span style={{ fontSize: 12, color: '#6d7175', whiteSpace: 'nowrap' }}><b style={{ color: '#202223' }}>{Math.round(100 * l.p)}%</b> OpsHub · {l.t}</span>
                                    </InlineStack>
                                    <StoreChart l={l} days={days} val={val} onTip={setTip} />
                                </div>);
                        })}
                    </div>
                    {table && (
                        <Card padding="0">
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}>
                                    <thead><tr>{['Store', 'Marks', 'OpsHub', 'Shopify', 'OpsHub share'].map((h, i) => <th key={h} style={{ textAlign: i ? 'right' : 'left', padding: '7px 10px', fontSize: 12, borderBottom: '1px solid #e1e3e5' }}>{h}</th>)}</tr></thead>
                                    <tbody>{locs.map(l => <tr key={l.name}>{[l.name, l.t, l.o, l.s, `${Math.round(100 * l.p)}%`].map((c, i) => <td key={i} style={{ textAlign: i ? 'right' : 'left', padding: '7px 10px', borderBottom: '1px solid #f1f2f3', fontVariantNumeric: 'tabular-nums' }}>{c}</td>)}</tr>)}</tbody>
                                </table>
                            </div>
                        </Card>)}
                    <Text as="p" variant="bodySm" tone="subdued">A mark counts as OpsHub when an OpsHub ready_for_pickup / picked_up event exists within 10 minutes of the time Shopify recorded the mark; otherwise it was done in Shopify. Days are in each store's local time. Corporate = company-owned retail stores (franchise stores excluded).</Text>
                </>)}
            {tip && (
                <div style={{ position: 'fixed', left: tip.x + 12, top: tip.y + 12, background: '#202223', color: '#fff', fontSize: 12, padding: '6px 9px', borderRadius: 6, pointerEvents: 'none', zIndex: 600, whiteSpace: 'nowrap' }}>
                    <b>{tip.l}</b> · {fmtDay(tip.d)}<br />OpsHub {tip.o} (ready {tip.r.ready.opshub}, picked up {tip.r.picked.opshub})<br />Shopify {tip.s} (ready {tip.r.ready.shopify}, picked up {tip.r.picked.shopify})
                </div>)}
        </BlockStack>
    );
}
